import { createSlice } from "@reduxjs/toolkit";
import type { MyRequest } from "../interfaces/requests.interface";
import {
  createRequest,
  fetchRequests,
  createMyRequest,
  updateRequestStatus,
  deleteRequest,
  uploadRequestImage,
} from "../thunks/requests.thunk";

/** Redux slice state for the requests feature. */
export interface RequestsState {
  requests: MyRequest[];
  isLoading: boolean;
  error: any | null;
  pagination: {
    page: number;
    totalPages: number;
    total: number;
    limit: number;
  };
  /** Granular loading flags for single-item actions. */
  createRequestLoading: boolean;
  updateStatusLoading: string | null;
  deleteRequestLoading: string | null;
}

const initialState: RequestsState = {
  requests: [],
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    totalPages: 1,
    total: 0,
    limit: 15,
  },
  createRequestLoading: false,
  updateStatusLoading: null,
  deleteRequestLoading: null,
};

const requestsSlice = createSlice({
  name: "requests",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearRequests: (state) => {
      state.requests = [];
      state.pagination = initialState.pagination;
    },
    clearMyRequestsError: (state) => {
      state.error = null;
    },
    clearMyRequests: (state) => {
      state.requests = [];
    },
    addRequest: (state, action) => {
      const list = Array.isArray(state.requests) ? state.requests : [];
      state.requests = [action.payload, ...list];
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Request
      .addCase(createRequest.pending, (state) => {
        state.createRequestLoading = true;
        state.error = null;
      })
      .addCase(createRequest.fulfilled, (state) => {
        state.createRequestLoading = false;
        state.error = null;
      })
      .addCase(createRequest.rejected, (state, action) => {
        state.createRequestLoading = false;
        state.error = action.payload;
      })

      // Fetch Requests (standard and "my" requests)
      .addCase(fetchRequests.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRequests.fulfilled, (state, action) => {
        state.isLoading = false;
        if (Array.isArray(action.payload?.data)) {
          state.requests = action.payload.data;
        }
        if (action.payload?.pagination) {
          state.pagination = {
            page: action.payload.pagination.page,
            totalPages: action.payload.pagination.totalPages,
            total: action.payload.pagination.total,
            limit: action.payload.pagination.limit,
          };
        }
        state.error = null;
      })
      .addCase(fetchRequests.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Create My Request
      .addCase(createMyRequest.pending, (state) => {
        state.createRequestLoading = true;
        state.error = null;
      })
      .addCase(createMyRequest.fulfilled, (state, action) => {
        state.createRequestLoading = false;
        if (action.payload) {
          state.requests = [action.payload, ...state.requests];
        }
        state.error = null;
      })
      .addCase(createMyRequest.rejected, (state, action) => {
        state.createRequestLoading = false;
        state.error = action.payload as string;
      })

      // Update Request Status
      .addCase(updateRequestStatus.pending, (state, action) => {
        state.updateStatusLoading = action.meta.arg.requestId;
      })
      .addCase(updateRequestStatus.fulfilled, (state, action) => {
        state.updateStatusLoading = null;
        const { requestId, updatedRequest } = action.payload;
        const idx = state.requests.findIndex((r) => r.id === requestId);
        if (idx !== -1 && updatedRequest) {
          state.requests[idx] = updatedRequest as MyRequest;
        }
      })
      .addCase(updateRequestStatus.rejected, (state, action) => {
        state.updateStatusLoading = null;
      })

      // Delete Request
      .addCase(deleteRequest.pending, (state, action) => {
        state.deleteRequestLoading = action.meta.arg;
      })
      .addCase(deleteRequest.fulfilled, (state, action) => {
        state.deleteRequestLoading = null;
        state.requests = state.requests.filter((r) => r.id !== action.payload);
      })
      .addCase(deleteRequest.rejected, (state) => {
        state.deleteRequestLoading = null;
      })
      // Upload request image
      .addCase(uploadRequestImage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(uploadRequestImage.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(uploadRequestImage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  clearRequests,
  clearMyRequestsError,
  addRequest,
  clearMyRequests,
} = requestsSlice.actions;

export default requestsSlice.reducer;
